'use client'
import type { FC } from 'react'
import { HStack, Icon } from '@chakra-ui/react'
import { BiInfoCircle } from 'react-icons/bi'
import { GrandText } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { PaymentProviderConfig, Option } from 'ecosystem'
import { useBuyerVat } from '../../common/hooks'

export interface VatNumberNoticeOverrides {
  vatNumberNoticeMissing?: string
  vatNumberNoticeInvalid?: string
  vatNumberNoticeReverseChargeUnverifiable?: string
}

interface VatNumberNoticeProps {
  paymentProviderConfig?: Option<PaymentProviderConfig>
}

/**
 * True when {@link VatNumberNotice} would render something.
 *
 * Callers inside a `Stack` with a `divider` must gate on this rather than
 * relying on the component's own null return: Chakra builds its dividers from
 * `React.Children.toArray`, which drops literal `null` branches but keeps an
 * element that merely *renders* null — drawing a divider on both sides of an
 * invisible child.
 */
export const useHasVatNumberNotice = (
  paymentProviderConfig?: Option<PaymentProviderConfig>
): boolean => Boolean(useBuyerVat(paymentProviderConfig).vatNumberIssue)

/**
 * Explains why a B2B buyer's VAT came out the way it did, whenever their VAT
 * number is the reason.
 *
 * Without this the two failure modes are silent. An EU company with a missing
 * or malformed number is charged destination VAT instead of being zero-rated,
 * and sees a number it did not expect with nothing to act on. And a company
 * explicitly set to reverse charge without a usable number produces an order
 * that the accounting push rejects outright — Fortnox drops the whole order,
 * writes nothing and never retries, so it simply never arrives.
 *
 * Renders nothing when there is no problem, which is the overwhelming majority
 * of checkouts (every consumer and every domestic company).
 */
export const VatNumberNotice: FC<VatNumberNoticeProps> = ({ paymentProviderConfig }) => {
  const overrides = useOverridesContext<keyof VatNumberNoticeOverrides>()
  const { vatNumberIssue } = useBuyerVat(paymentProviderConfig)

  if (!vatNumberIssue) {
    return null
  }

  // Reverse charge with an unusable number is the one that loses the order, so
  // it reads as a warning rather than an explanation.
  const isBlocking = vatNumberIssue === 'REVERSE_CHARGE_UNVERIFIABLE'

  const message = (() => {
    switch (vatNumberIssue) {
      case 'MISSING':
        return overrideText(
          'Företaget har inget momsregistreringsnummer registrerat, därför debiteras moms enligt ditt lands skattesats.',
          overrides?.vatNumberNoticeMissing
        )
      case 'INVALID_FORMAT':
        return overrideText(
          'Momsregistreringsnumret ser inte giltigt ut – det ska inledas med landskoden.',
          overrides?.vatNumberNoticeInvalid
        )
      case 'REVERSE_CHARGE_UNVERIFIABLE':
        return overrideText(
          'Företaget är inställt på omvänd skattskyldighet, men momsregistreringsnumret saknas eller är felaktigt.',
          overrides?.vatNumberNoticeReverseChargeUnverifiable
        )
    }
  })()

  return (
    <HStack
      align="flex-start"
      bg={isBlocking ? 'status.warning2' : 'background.surface'}
      borderRadius="sm"
      color={isBlocking ? 'text.default' : 'text.mild'}
      data-testid="vat-number-notice"
      p={3}
      spacing={2}
      w="full"
    >
      {/* Plain `Icon`, not `GrandText` — the latter is hardcoded to `w="100%"`,
          which on a flex row makes the icon claim the whole width and pushes the
          message out of the box. */}
      <Icon aria-hidden as={BiInfoCircle} boxSize={4} flexShrink={0} mt="2px" />
      <GrandText fontSize="sm">{message}</GrandText>
    </HStack>
  )
}
